const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"]
const MONEY_FORMAT = '#,##0.00;[Red]-#,##0.00'

function excelCell(value, style = {}) {
  return {
    value: value === null || value === undefined ? "" : value,
    type: typeof value === "number" ? Number : String,
    ...style,
  }
}

export function buildTransactionYoyCenterExport({
  data,
  selectedStudios = [],
  viewMode = "combined",
  compareYearA,
  compareYearB,
  dataType = "invoices",
  deductVAT = false,
  vatRate = 0.081,
  exportedAt = new Date(),
} = {}) {
  const results = data?.results || {}
  const centers = Object.keys(results)
    .filter((name) => name !== "Gesamt" && (selectedStudios.includes("Gesamt") || selectedStudios.includes(name)))
    .sort((left, right) => left.localeCompare(right, "de-CH"))
  const availableYears = [...new Set((data?.years || []).map(Number))].filter(Number.isInteger)
  const comparisonYears = [Number(compareYearA), Number(compareYearB)]
  const validComparison = comparisonYears[0] !== comparisonYears[1] && comparisonYears.every((year) => availableYears.includes(year))
  const years = (viewMode === "custom" ? (validComparison ? comparisonYears : []) : availableYears).sort((left, right) => right - left)
  const isV2 = data?.version === "v2"
  const isNet = dataType === "payments" && deductVAT
  const divisor = isNet ? 1 + vatRate : 1
  const exportDate = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Zurich" }).format(exportedAt)
  const asOfDate = isV2 ? String(data?.snapshot?.source?.asOfDate || "").slice(0, 10) : exportDate
  const basis = isV2 ? "V2 RADB" : "V1 bisher"
  const metric = dataType === "payments" ? "Cash-Eingang" : "Debitoren"
  const vatLabel = isNet ? `Netto (exkl. ${(vatRate * 100).toFixed(1)}% MWST)` : "Brutto"
  const headers = ["Center", "Jahr", ...MONTHS, "Jahressumme (CHF)", "Hinweise zum Datenstand"]
  const rows = [headers.map((value) => excelCell(value, { fontWeight: "bold", color: "#ffffff", backgroundColor: "#1f2937", height: 28 }))]

  centers.forEach((center, centerIndex) => {
    years.forEach((year) => {
      const coverage = isV2 ? data?.coverage?.months?.[year] : null
      const missing = []
      const partial = []
      const future = []
      const months = MONTHS.map((month, index) => {
        const status = coverage?.[index]
        if (status === "partial") partial.push(month)
        if (status === "future") future.push(month)
        const rawValue = results[center]?.[year]?.[index]
        const covered = !isV2 || ["complete", "partial"].includes(status)
        if (!covered || rawValue === null || rawValue === undefined || rawValue === "" || !Number.isFinite(Number(rawValue))) {
          if (status !== "future") missing.push(month)
          return null
        }
        return Number(rawValue) / divisor
      })
      const values = months.filter((value) => value !== null)
      const total = values.length ? values.reduce((sum, value) => sum + value, 0) : null
      const notes = []
      if (!isV2 && year === Number(asOfDate.slice(0, 4))) notes.push(`Laufendes Jahr bis ${asOfDate}`)
      if (missing.length) notes.push(`Ohne Daten: ${missing.join(", ")}`)
      if (partial.length) notes.push(`Teilmonat: ${partial.join(", ")}`)
      if (future.length) notes.push(`Nach Datenstand: ${future.join(", ")}`)
      const backgroundColor = centerIndex % 2 === 0 ? "#f1f5f9" : "#ffffff"
      rows.push([center, year, ...months, total, notes.join(" · ")].map((value, index) => excelCell(value, {
        backgroundColor,
        ...(index === 1 ? { format: "0" } : {}),
        ...(index >= 2 && index <= 14 ? { format: MONEY_FORMAT, align: "right" } : {}),
        ...(index === 0 || index === 14 ? { fontWeight: "bold" } : {}),
      })))
    })
  })

  const information = [
    ["Auswertung", "Monatlicher Umsatzvergleich pro Center"],
    ["Datenbasis", basis],
    ["Kennzahl", metric],
    ["Beträge", `${vatLabel} · CHF`],
    ["Jahre", years.join(", ")],
    ["Center", centers.join(", ")],
    ["Datenstand", asOfDate || "Nicht verfügbar"],
    ["Exportiert am", exportedAt.toLocaleString("de-CH", { timeZone: "Europe/Zurich" })],
    ["Jahressumme", "Summe der verfügbaren Monatswerte; das laufende Jahr reicht nur bis zum Datenstand."],
    ["Auswahl", "Gesamt wird in die einzelnen verfügbaren Center aufgeteilt. Die Gesamtzeile wird nicht zusätzlich exportiert."],
    ["Monatswerte", isV2
      ? "Leere Zellen bedeuten fehlende Historie oder Monate nach dem RADB-Stand. Teilmonate sind in der Hinweisspalte gekennzeichnet."
      : "Die Werte entsprechen dem V1-Jahresvergleich. V1 liefert Monate ohne erfasste Werte als 0."],
  ]
  if (isV2) information.push(["Snapshot", data?.snapshot?.id || "Nicht verfügbar"])

  return {
    centerCount: centers.length,
    yearCount: years.length,
    sheets: [rows, information.map(([label, value]) => [excelCell(label, { fontWeight: "bold" }), excelCell(value)])],
    options: {
      sheets: ["Monatsumsatz", "Informationen"],
      columns: [
        [{ width: 38 }, { width: 10 }, ...MONTHS.map(() => ({ width: 18 })), { width: 23 }, { width: 80 }],
        [{ width: 24 }, { width: 120 }],
      ],
    },
    fileName: `monatsumsatz-pro-center_${isV2 ? "v2-radb" : "v1"}_${dataType === "payments" ? "cash-eingang" : "debitoren"}_${isNet ? "netto" : "brutto"}_${[...years].reverse().join("-")}_${exportDate}`,
  }
}
